// src/components/publishing/available-servers-strip.tsx
import React from "react";
import {
  FileCode2, Globe, Linkedin, AlertTriangle, Clock, CheckCircle2,
  Settings2, Loader2,
} from "lucide-react";
import {
  Destination, destinationLabel, Integration,
  daysUntilExpiry,
} from "@/app/services/publishing/publishing-api";

interface AvailableServersStripProps {
  integrations: Integration[];
  loading?: boolean;
  onManageClick?: () => void;
}

/**
 * Read-only strip shown on the Brand Publisher page. Displays the user's
 * connected destinations ("MCP servers") with status chips. Does NOT contain
 * connect/disconnect controls — those live in the Integrations page.
 *
 * Only destinations the user has actually connected appear here. If none are
 * connected, we show a single empty state with a CTA to the Integrations page.
 */
export function AvailableServersStrip({
  integrations, loading, onManageClick,
}: AvailableServersStripProps) {
  const connected = integrations.filter(
    (i): i is Integration => !!i,
  );

  if (loading) {
    return (
      <div
        style={{
          padding: "14px 16px", borderRadius: 10,
          border: "1px solid var(--border)",
          backgroundColor: "var(--muted)",
          display: "flex", alignItems: "center", gap: 10,
          color: "var(--muted-foreground)",
          fontSize: 13,
        }}
      >
        <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
        Loading available servers…
      </div>
    );
  }

  if (connected.length === 0) {
    return (
      <div
        style={{
          padding: 16, borderRadius: 10,
          border: "1px dashed var(--border)",
          backgroundColor: "var(--muted)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: 6,
        }}>
          <AlertTriangle style={{ width: 16, height: 16, color: "#D97706" }} />
          <span style={{
            fontSize: 13, fontWeight: 700, color: "var(--foreground)",
          }}>
            No publishing servers connected yet
          </span>
        </div>
        <p style={{
          fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5,
          margin: "0 0 10px 0",
        }}>
          To publish content, connect at least one destination (Hashnode,
          WordPress, or LinkedIn) from the Integrations page.
        </p>
        {onManageClick && (
          <button
            onClick={onManageClick}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "7px 13px", borderRadius: 7,
              border: "1px solid rgba(13,148,136,0.25)",
              backgroundColor: "rgba(13,148,136,0.08)",
              color: "#0D9488",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            <Settings2 style={{ width: 12, height: 12 }} />
            Go to Integrations
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "10px 12px", borderRadius: 10,
        border: "1px solid var(--border)",
        backgroundColor: "var(--muted)",
      }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, marginBottom: 8, flexWrap: "wrap",
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: "var(--muted-foreground)",
          textTransform: "uppercase", letterSpacing: "0.05em",
        }}>
          Available servers ({connected.length})
        </div>
        {onManageClick && (
          <button
            onClick={onManageClick}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "4px 8px", borderRadius: 6,
              border: "1px solid var(--border)",
              backgroundColor: "var(--background)",
              color: "var(--muted-foreground)",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}
          >
            <Settings2 style={{ width: 10, height: 10 }} />
            Manage
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {connected.map(i => <ServerChip key={i.id} integration={i} />)}
      </div>
    </div>
  );
}

// ─── Individual server chip ──────────────────────────────────────────────────

function ServerChip({ integration }: { integration: Integration }) {
  const meta = {
    hashnode:  { Icon: FileCode2, color: "#2962FF" },
    wordpress: { Icon: Globe,     color: "#21759B" },
    linkedin:  { Icon: Linkedin,  color: "#0A66C2" },
  }[integration.destination as Destination];

  const days = daysUntilExpiry(integration.expires_at);
  const isExpired = integration.status !== "active" || (days !== null && days <= 0);
  const isExpiringSoon = integration.status === "active" && days !== null && days > 0 && days < 7;

  const accent = isExpired ? "#EF4444"
    : isExpiringSoon ? "#D97706"
    : "#10B981";

  const bgTint = isExpired ? "rgba(239,68,68,0.06)"
    : isExpiringSoon ? "rgba(245,158,11,0.06)"
    : "rgba(16,185,129,0.06)";

  const borderColor = isExpired ? "rgba(239,68,68,0.3)"
    : isExpiringSoon ? "rgba(245,158,11,0.3)"
    : "rgba(16,185,129,0.25)";

  const subLabel = integration.account_metadata?.publication_title
    || integration.account_metadata?.display_name
    || (integration.account_metadata?.site_url
         ? integration.account_metadata.site_url.replace(/^https?:\/\//, "")
         : null);

  return (
    <div
      title={
        isExpired
          ? `${destinationLabel(integration.destination as Destination)} — credentials expired`
          : isExpiringSoon
            ? `${destinationLabel(integration.destination as Destination)} — expires in ${days} day${days === 1 ? "" : "s"}`
            : `${destinationLabel(integration.destination as Destination)} — connected`
      }
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "7px 11px", borderRadius: 8,
        backgroundColor: bgTint,
        border: `1px solid ${borderColor}`,
        maxWidth: 280,
      }}
    >
      <div
        style={{
          width: 22, height: 22, borderRadius: 5,
          backgroundColor: "var(--background)",
          border: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <meta.Icon style={{ width: 12, height: 12, color: meta.color }} />
      </div>
      <div style={{
        display: "flex", flexDirection: "column", minWidth: 0,
        lineHeight: 1.25,
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 12, fontWeight: 700, color: "var(--foreground)",
        }}>
          {destinationLabel(integration.destination as Destination)}
          {isExpired && <AlertTriangle style={{ width: 10, height: 10, color: accent }} />}
          {isExpiringSoon && !isExpired && <Clock style={{ width: 10, height: 10, color: accent }} />}
          {!isExpired && !isExpiringSoon && <CheckCircle2 style={{ width: 10, height: 10, color: accent }} />}
        </div>
        {subLabel && (
          <div style={{
            fontSize: 10, color: "var(--muted-foreground)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: 240,
          }}>
            {subLabel}
          </div>
        )}
      </div>
    </div>
  );
}